import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ClipboardList,
  MapPin,
  Users,
  LogOut,
  Wrench,
  Menu,
  X,
  Briefcase,
  Package,
  BarChart3,
  Bell,
  CalendarDays,
} from "lucide-react";

const navAdmin = [
  { to: "/", label: "All Issues", icon: ClipboardList, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/reports", label: "Cost Reports", icon: BarChart3 },
  { to: "/locations", label: "Locations", icon: MapPin },
  { to: "/vendors", label: "Vendors", icon: Briefcase },
  { to: "/parts", label: "Parts", icon: Package },
  { to: "/users", label: "Users", icon: Users },
  { to: "/settings", label: "Notifications", icon: Bell },
];

const navManager = [
  { to: "/", label: "Issues", icon: ClipboardList, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
];

const navLocation = [
  { to: "/", label: "My Issues", icon: ClipboardList, end: true },
  { to: "/schedule", label: "Schedule", icon: CalendarDays },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = user?.role === "admin" ? navAdmin : user?.role === "manager" ? navManager : navLocation;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD]">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#0055FF] text-white flex items-center justify-center">
            <Wrench size={16} strokeWidth={2.4} />
          </div>
          <span className="font-display font-bold tracking-tight">FixBoard</span>
        </div>
        <Button
          data-testid="mobile-menu-toggle"
          variant="ghost"
          size="icon"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-6 h-20 flex items-center gap-3 border-b border-gray-100">
          <div className="w-10 h-10 rounded-lg bg-[#0055FF] text-white flex items-center justify-center shadow-sm">
            <Wrench size={18} strokeWidth={2.4} />
          </div>
          <div>
            <p className="font-display font-bold text-lg leading-none tracking-tight">FixBoard</p>
            <p className="text-xs text-gray-500 mt-1 tracking-wider uppercase">Maintenance OS</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0055FF] text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-700">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start"
          >
            <LogOut size={14} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-20 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <main className="lg:ml-64 min-h-screen">{children}</main>
    </div>
  );
}
